/* eslint-disable camelcase */
/* eslint-disable max-statements */
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import type { DataTable as DataTableRefType } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { metricValueFormat } from '../../utils/metricValueFormat';
import { NameBodyTemplate,  TruncatedTextTemplate } from './nameBodyTemplate';
import './stackRankDashboardTable.scss';
import { selectCurrentEmpid } from '../../pages/login/authSlice';
import { exportExcel } from './stackrank-dashboard-view-table-service';
import { TeamLeadviews, View, views } from '../../pages/stackRank/types';
import { ConstScorecardRole } from '../../utils/typeConstants';
import Calculations from '../../assets/Calculations.png';
import { StackRankCalculation } from '../../features/stackRankCalculation/StackRankCalculation';
import { isTieredScorecard } from '../../utils/metricAttainment';
import { StackRankGridColors } from '../../utils/stackRankColors';

export const createMenuItemTemplate = (alt: string, label: string, onClick: () => void) => {
    const getImageByLabel = (label: string) => {
        switch (label) {
            case 'View Calculations':
                return Calculations;
            default:
                return '';
        }
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };
    const imageSrc = getImageByLabel(label);

    return () => (
        <div
            className="p-menuitem-link"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={handleKeyDown}
        >
            <img src={imageSrc} alt={alt} style={{ width: 16, marginRight: 8 }} />
            <span>{label}</span>
        </div>
    );
};

interface PropTypes {
    stackRankSearchData?: any;
    selectedMetric?: string;
    setMetricContent: React.Dispatch<any>;
    setSelectedUserInGrid: React.Dispatch<any>;
    selectedUserInGrid?: any;
    setMetricSmiley: React.Dispatch<any>;
    setSelectedManager: React.Dispatch<any>;
    setSelectedSupervisor: React.Dispatch<any>;
    setSelectedView: React.Dispatch<any>;
    supervisors?: any;
    managers?: any;
    scorecardRoleId?: number;
    inputMonth?: number;
    inputYear?: number;
    scorecardVersionID?: number | string;
    showNonPayoutMetrics?: boolean;
    setShowNonPayoutMetrics?: React.Dispatch<React.SetStateAction<boolean>>;
    shouldShowToggle?: boolean;
    // P4P multi-tier (E20-6317): resolved hex for the currently selected metric's color family
    // (StackRankBandColors[colorIndex].meets), so the selected-row highlight matches the metric
    // being viewed instead of one fixed color. Only applied to rows whose own
    // scorecard_calculation_rule_id is tiered - legacy rows are untouched.
    selectedRowColor?: string;

}
export const StackRankDashboardTable = ({
    stackRankSearchData,
    selectedMetric,
    setMetricContent,
    setSelectedUserInGrid,
    selectedUserInGrid,
    setMetricSmiley,
    setSelectedManager,
    setSelectedSupervisor,
    setSelectedView,
    supervisors,
    managers,
    scorecardRoleId,
    inputMonth,
    inputYear,
    scorecardVersionID,
    showNonPayoutMetrics,
    setShowNonPayoutMetrics,
    shouldShowToggle = true,
    selectedRowColor,
}: PropTypes) => {
    const uniqueKey = 'employee_number';
    const loggedInUser = useSelector(selectCurrentEmpid);
    // P4P multi-tier (E20-6317): whether the currently viewed scorecard is tiered - computed
    // from the data already passed in (same pattern as rowClassName/metricsOneBodyTemplateDispatch
    // below), rather than a separate prop, since every row shares one scorecard version per view.
    const isTieredView = isTieredScorecard(stackRankSearchData?.[0]?.scorecard_calculation_rule_id);
    const [agents, setAgents] = useState<any>(null);
    const [metricColHeader, setMetricColHeader] = useState<any>([]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [hideTable, setHideTable] = useState<boolean>(true);
    const [loggedInUserRow, setLoggedInUserRow] = useState<any>(selectedUserInGrid);
    const [selectedRow, setSelectedRow] = useState<any>(selectedUserInGrid);
    const [view, setView] = useState<View>({ name: 'Global', code: '1' });
    const [supervisor, setSupervisor] = useState<string | null>(null);
    const [manager, setManager] = useState<string | null>(null);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(50);
    const tableRef = useRef<DataTableRefType>(null);
    const menu = useRef<Menu>(null);
    const [calculationVisible, setCalculationVisible] = useState<boolean>(false);

    // eslint-disable-next-line no-unsafe-optional-chaining
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        employee_name: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
        },
    });

    const onCalculationClicked = () => {
        setCalculationVisible(true);
    };

    const items = [];
    items.push(
        {
            label: 'View Calculations',
            template: createMenuItemTemplate('View Calculations', 'View Calculations', onCalculationClicked),
        }
    );

    const popupMenuClickHandler = (e: any, rowData: any) => {
        e.preventDefault();
        setSelectedRow(rowData);
        menu.current?.toggle(e);
    };

    useEffect(() => {
        setSelectedUserInGrid(loggedInUserRow);
        setSelectedRow(loggedInUserRow);
    }, [stackRankSearchData]);

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        const afilters = { ...filters };
        afilters.global.value = value;
        setFilters(afilters);
        setGlobalFilterValue(value);
    };

    const onRowClick = (e: any) => {
        const clickedRow = e.data;
        if (selectedRow && selectedRow === clickedRow) {
            setSelectedRow(loggedInUserRow);
            setSelectedUserInGrid(loggedInUserRow);
            scrollToAgent(loggedInUserRow?.employee_number);
            return;
        }
        setSelectedRow(clickedRow);
        setSelectedUserInGrid(clickedRow);
        scrollToAgent(clickedRow.employee_number);
    };

    const getNumberWithSuffix = (num: number) => {
        const lastTwoDigits = num % 100;
        if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
            return num + 'th';
        }
        switch (num % 10) {
            case 1:
                return num + 'st';
            case 2:
                return num + 'nd';
            case 3:
                return num + 'rd';
            default:
                return num + 'th';
        }
    };
    const getMetricDetailsText = () => {
        setMetricSmiley('neutral');
        if (!selectedRow || Object.keys(selectedRow).length === 0)
            return 'Stack Rank displays a ranking of associates based on a calculated composite score. This score is determined by the percentage of target attainment for each metric in the scorecard.';

        const employeeName = selectedRow?.employee_name || '';
        let name = '';
        let firstName = '';

        if (employeeName.startsWith('Anonymous')) {
            name = employeeName;
            firstName = 'Anonymous';
        } else {
            name = `${selectedRow?.first_name} ${selectedRow?.last_name}`;
            firstName = selectedRow?.first_name;
        }

        const rank =
            Object.keys(selectedRow).length > 0 &&
            selectedRow?.overall_rank &&
            selectedRow?.overall_rank !== 'null'
                ? getNumberWithSuffix(selectedRow?.overall_rank)
                : ' ';
        const percentile = selectedRow?.rank_percentile || 0;
        const percentilerank = parseFloat((percentile * 100).toFixed(0));
        const percentilerankwithSuffix =
            Object.keys(selectedRow).length > 0 ? getNumberWithSuffix(percentilerank) : ' ';
        let className = '';
        if (percentilerank > 70) className = 'top-rank';
        else if (percentilerank >= 30 && percentilerank <= 70) className = 'medium-rank';
        else if (percentilerank < 30 && selectedRow?.rank_percentile) className = 'low-rank';
        else className = 'neutral-rank';
        let metricDetailsText = '';
        if (rank === ' ')
            metricDetailsText = `${name} does not have enough calculated data to be ranked at this point.`;
        else if (selectedRow?.employee_number === loggedInUser)
            metricDetailsText = `Hello ${name}, you are <span class=${className}>${rank}</span> in the general ranking. You are in the <span class=${className}>${percentilerankwithSuffix}</span> percentile.`;
        else
            metricDetailsText = `${name} is <span class=${className}>${rank}</span> in the general ranking. ${firstName} is in the <span class=${className}>${percentilerankwithSuffix}</span> percentile.`;
        if (percentilerank > 70) {
            setMetricSmiley('top');
            if (selectedRow?.employee_number === loggedInUser)
                metricDetailsText += ` You have exceeded the average for this month and you are ahead within your team.`;
            else
                metricDetailsText += ` ${firstName} has exceeded the average for this month and is ahead within the team.`;
        } else if (percentilerank >= 30 && percentilerank <= 70) {
            setMetricSmiley('medium');
            if (selectedRow?.employee_number === loggedInUser)
                metricDetailsText += ` You have met the average for this month and are currently in the middle of the rankings within your team.`;
            else
                metricDetailsText += ` ${firstName} has met the average for this month and is currently in the middle of the rankings within the team.`;
        } else if (percentilerank < 30 && selectedRow?.rank_percentile) {
            setMetricSmiley('bottom');
            if (selectedRow?.employee_number === loggedInUser)
                metricDetailsText += ` You are below average for this month and you are behind within your team.`;
            else
                metricDetailsText += ` ${firstName} is below average for this month and is behind within the team.`;
        }
        return metricDetailsText;
    };
    const rowClassName = (rowData: any) => {
        let className = '';
        if (rowData.employee_number === loggedInUser) {
            if (selectedRow != null && Object.keys(selectedRow).length === 0) {
                setSelectedRow(rowData);
                setSelectedUserInGrid(rowData);
                setLoggedInUserRow(rowData);
            }
            className = 'logged-in-user-row';
        } else if (rowData.employee_number === selectedRow?.employee_number) {
            // P4P multi-tier (E20-6317): the tiered class carries shape only (no color, unlike
            // 'selected-row' which uses !important colors) - actual color comes from rowStyle
            // below, driven by the currently selected metric, not a fixed hex.
            className = isTieredScorecard(rowData?.scorecard_calculation_rule_id)
                ? 'selected-row-tiered'
                : 'selected-row';
        }
        return className;
    };

  const renderHeader = () => {
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="content rounded-border w-300">
                        <label htmlFor="name">View</label>
                        {scorecardRoleId === ConstScorecardRole.ASSOCIATE && (
                            <Dropdown
                                value={view}
                                onChange={(e) => {
                                    setView(e.value);
                                    setSelectedView(e.value);
                                }}
                                options={views}
                                optionLabel="name"
                                placeholder="Select a View"
                                className="custom-dropdown w-200"
                            />
                        )}
                        {scorecardRoleId === ConstScorecardRole.TEAM_LEAD && (
                            <Dropdown
                                value={view}
                                onChange={(e) => {
                                    setView(e.value);
                                    setSelectedView(e.value);
                                }}
                                options={TeamLeadviews}
                                optionLabel="name"
                                placeholder="Select a View"
                                className="custom-dropdown w-200"
                            />
                        )}
                    </div>
                    {view?.code === '2' && (
                        <div className="content rounded-border w-300">
                            {scorecardRoleId === ConstScorecardRole.ASSOCIATE && (
                                <>
                                    {' '}
                                    <label htmlFor="TL">Select Team Lead</label>
                                    <Dropdown
                                        value={supervisor}
                                        options={supervisors}
                                        onChange={(e) => {
                                            setSupervisor(e.value);
                                            setSelectedSupervisor(e.value);
                                        }}
                                        placeholder="Select a Supervisor"
                                        className="custom-dropdown"
                                    />
                                </>
                            )}
                            {scorecardRoleId === ConstScorecardRole.TEAM_LEAD && (
                                <>
                                    {' '}
                                    <label htmlFor="TL">Select Manager</label>
                                    <Dropdown
                                        value={supervisor}
                                        options={supervisors}
                                        onChange={(e) => {
                                            setSupervisor(e.value);
                                            setSelectedSupervisor(e.value);
                                        }}
                                        placeholder="Select a Manager"
                                        className="custom-dropdown"
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {scorecardRoleId === ConstScorecardRole.ASSOCIATE && view?.code === '3' && (
                        <div className="content rounded-border w-300">
                            <label htmlFor="Manager">Select Manager</label>
                            <Dropdown
                                value={manager}
                                options={managers}
                                onChange={(e) => {
                                    setManager(e.value);
                                    setSelectedManager(e.value);
                                }}
                                placeholder="Select a Manager"
                                className="custom-dropdown"
                            />
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {/* Search Input */}
                    <div className="content rounded-border">
                        <label htmlFor="Manager">Search</label>
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText
                                style={{ borderRadius: '20px' }}
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                                placeholder="Keyword Search"
                            />
                        </span>
                    </div>
                    <Button
                        type="button"
                        icon="pi pi-upload me-2"
                        className="btn-export mt-4"
                        onClick={() => exportExcel(agents, metricColHeader)}
                        data-pr-tooltip="XLS"
                    >
                        Export
                    </Button>
                </div>
            </div>
            
            {shouldShowToggle && (
            <div style={{ marginTop: '1rem' }}>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <label 
                        htmlFor="non-payout-toggle" 
                        style={{ 
                            margin: 0, 
                            fontWeight: 500, 
                            fontSize: '14px',
                            color: '#7D71E2',
                            cursor: 'pointer'
                        }}
                    >
                        Show Non-Payout Metrics
                    </label>
                    <InputSwitch 
                        inputId="non-payout-toggle"
                        checked={showNonPayoutMetrics} 
                        onChange={(e) => setShowNonPayoutMetrics && setShowNonPayoutMetrics(e.value)}
                        className="stack-rank-toggle"
                    />
                </div>
            </div>
            )}
        </div>
    );
};

    const header = renderHeader();
    useEffect(() => {
        if (Array.isArray(stackRankSearchData) && stackRankSearchData.length > 0) {
            setAgents(stackRankSearchData);
            scrollToAgent(selectedUserInGrid?.employee_number);
        } else {
            setAgents([]);
        }
    }, [stackRankSearchData, loggedInUser]);

    const getHeaderClassName = (columnName: string) => {
        if (columnName === selectedMetric) {
            // P4P multi-tier (E20-6317): tiered gets a class with no fixed color (structural
            // bold/size only) - the actual color comes from headerStyle below, driven by the
            // currently selected metric's accent color, same value used for the selected row.
            // Legacy keeps the untouched 'highlighted-header' (fixed teal).
            return isTieredView ? 'highlighted-header-tiered' : 'highlighted-header';
        }
        return '';
    };

    const getHeaderStyle = (columnName: string): React.CSSProperties | undefined => {
        if (columnName === selectedMetric && isTieredView && selectedRowColor) {
            return { color: selectedRowColor };
        }
        return undefined;
    };

    useEffect(() => {
        if (stackRankSearchData.length > 0) {
            stackRankSearchData.map((x: any) => {
                const obj = Object.keys(x?.agentMetrics[0]?.metricsDetails);
                setMetricColHeader(obj);
                return obj;
            });
        }
        if (agents?.length > 0) setHideTable(false);
        else setHideTable(true);
    }, [stackRankSearchData, agents]);

    useEffect(() => {
        setMetricContent(getMetricDetailsText());
    }, [selectedMetric, selectedRow]);

    const metricsOneBodyTemplate = (rowData: any, props: any) => {
        let metricValue = rowData?.agentMetrics[0]?.metricsDetails[props?.column?.props.header]?.metric_value;
        let metricPercentToTarget =
            rowData?.agentMetrics[0]?.metricsDetails[props?.column?.props.header]?.percent_to_target || 0;
        metricPercentToTarget = parseFloat((metricPercentToTarget * 100).toFixed(0));
        const metricTypeName =
            rowData?.agentMetrics[0]?.metricsDetails[props?.column?.props.header]?.metric_type_name;
        if (metricValue === undefined || metricValue === null) {
            metricValue = '--';
        } else if (metricTypeName !== undefined) {
            metricValue = metricValueFormat(metricValue, metricTypeName);
        }

        // Determine color based on metric value
        let colorClass = '';
        if (metricPercentToTarget >= 100) {
            colorClass = 'more-than-100-performance';
        } else if (metricPercentToTarget >= 70 && metricPercentToTarget < 100) {
            colorClass = 'less-than-100-performance';
        } else if (metricPercentToTarget >= 0 && metricPercentToTarget < 70) {
            colorClass = 'less-than-70-performance';
        }

        const metricElement = <span className={`image-text ${colorClass}`}>{metricValue}</span>;
        return metricElement;
    };

    /**
     * P4P multi-tier (E20-6317) grid cell coloring - separate function, metricsOneBodyTemplate
     * above is untouched. Fixed 3-color scheme (Below Meets=red, Meets=blue, Exceeds=green),
     * independent of metric identity - unlike the tiered gauge/chart, which colors per metric
     * family. Keyed primarily off metric_attainment_level_label, falling back to attainment_rank
     * only if the label is missing/unrecognized - same pattern as getTieredStatusVisual.
     */
    const metricsOneBodyTemplateTiered = (rowData: any, props: any) => {
        const metricInfo = rowData?.agentMetrics[0]?.metricsDetails[props?.column?.props.header];
        let metricValue = metricInfo?.metric_value;
        const metricTypeName = metricInfo?.metric_type_name;
        if (metricValue === undefined || metricValue === null) {
            metricValue = '--';
        } else if (metricTypeName !== undefined) {
            metricValue = metricValueFormat(metricValue, metricTypeName);
        }

        const label = metricInfo?.metric_attainment_level_label;
        const rank = metricInfo?.attainment_rank;
        let color = StackRankGridColors.meets;
        if (label === 'Below Meets') {
            color = StackRankGridColors.belowMeets;
        } else if (label === 'Exceeds Target') {
            color = StackRankGridColors.exceeds;
        } else if (label === 'Meets Target') {
            color = StackRankGridColors.meets;
        } else if (rank != null) {
            if (rank <= 0) {
                color = StackRankGridColors.belowMeets;
            } else if (rank >= 2) {
                color = StackRankGridColors.exceeds;
            }
        }

        return <span className="image-text" style={{ color, fontWeight: 600 }}>{metricValue}</span>;
    };

    // Per-row dispatch, not per-column/table - scorecard_calculation_rule_id lives on the row
    // (StackRankResult), so different rows in principle could differ. metricsOneBodyTemplate
    // (legacy) is passed through completely untouched for non-tiered rows.
    const metricsOneBodyTemplateDispatch = (rowData: any, props: any) => {
        if (isTieredScorecard(rowData?.scorecard_calculation_rule_id)) {
            return metricsOneBodyTemplateTiered(rowData, props);
        }
        return metricsOneBodyTemplate(rowData, props);
    };

    const formatPercentage = (rowData: any) => {
        const metricValue = rowData?.composite_score;
        if (metricValue === undefined || metricValue === null) {
            return '--';
        }
        return `${(metricValue * 100)?.toFixed(2)}%`;
    };
    const formatAttainment = (rowData: any) => {
        const totalAttained = rowData.total_metrics_attained || 0;
        const totalReported = rowData.total_metrics_reported || 0;

        const attainmentValue = `${totalAttained}/${totalReported}`;

        const diff = totalReported - totalAttained;

        let colorClass = '';
        if (totalAttained <= 0) {
            colorClass = 'zero-attainment';
        } else if (diff <= 1) {
            colorClass = 'more-attainment';
        } else {
            colorClass = '';
        }
        const attainmentElement = <span className={colorClass}>{attainmentValue}</span>;

        return attainmentElement;
    };
    const customSortFunction = (event: any, metricHeader: string) => {
        const { data, order } = event;

        return data.sort((a: any, b: any) => {
            const valueA = a?.agentMetrics[0]?.metricsDetails[metricHeader]?.metric_value;
            const valueB = b?.agentMetrics[0]?.metricsDetails[metricHeader]?.metric_value;

            // Check for nulls/undefined/empty values
            const isAEmpty = valueA === undefined || valueA === null || valueA === '--';
            const isBEmpty = valueB === undefined || valueB === null || valueB === '--';

            // Always keep nulls at the end regardless of sort direction
            if (isAEmpty && !isBEmpty) return 1; // A is null, B is not - A goes last
            if (!isAEmpty && isBEmpty) return -1; // B is null, A is not - B goes last
            if (isAEmpty && isBEmpty) return 0; // Both null - maintain original order

            // Now we know both values exist, proceed with normal comparison
            // Convert to numbers if they're numeric
            const numA = parseFloat(valueA);
            const numB = parseFloat(valueB);

            if (!isNaN(numA) && !isNaN(numB)) {
                return (numA - numB) * order;
            }

            // String comparison if not numbers
            if (valueA < valueB) return -1 * order;
            if (valueA > valueB) return 1 * order;
            return 0;
        });
    };
    const scrollToAgent = (loggedUser: any) => {
        if (!stackRankSearchData?.length) return;
        const index = stackRankSearchData.findIndex((a: any) => a.employee_number === loggedUser);
        if (index === -1) return;

        const page = Math.floor(index / rows);
        const localRowIndex = index % rows;

        setFirst(page * rows);

        let attempts = 0;
        const maxAttempts = 3;

        const tryScroll = () => {
            const tableElement = tableRef.current?.getElement();
            const scrollableBody = tableElement?.querySelector('.p-datatable-wrapper') as HTMLElement;
            const rowElements = scrollableBody?.querySelectorAll('.p-datatable-tbody > tr');

            const headElement = scrollableBody?.querySelector('.p-datatable-thead') as HTMLElement;
            const theadRect = headElement?.getBoundingClientRect();
            const headHeight = theadRect?.height || 0;

            if (scrollableBody && rowElements && rowElements.length > localRowIndex) {
                const row = rowElements[localRowIndex] as HTMLElement;

                if (row.offsetTop >= 0) {
                    const rowHeight = row.offsetHeight;
                    const HEADER_HEIGHT = headHeight + 40;
                    const desiredScrollTop = row.offsetTop - (HEADER_HEIGHT + 2 * rowHeight);

                    scrollableBody.scrollTop = desiredScrollTop;
                    return;
                }
            }

            if (attempts < maxAttempts) {
                attempts++;
                setTimeout(tryScroll, 100);
            }
        };

        setTimeout(() => {
            requestAnimationFrame(tryScroll); // Ensures DOM is ready
        }, 150);
    };

    const renderCalculationModels = () => {
        return (
            <StackRankCalculation
                setDisplayModal={setCalculationVisible}
                displayModal={calculationVisible}
                authorId={loggedInUser}
                seletedRowData={selectedRow}
                inputMonth={inputMonth}
                inputYear={inputYear}
                scorecardVersionID={scorecardVersionID}
            />
        );
    };

    const actionTemplate = (rowData: any) => {
        if (rowData?.composite_score) {
            return (
                <Button
                    type="button"
                    icon="pi pi-ellipsis-v"
                    onClick={(e: any) => {
                        popupMenuClickHandler(e, rowData);
                    }}
                />
            );
        } else return '';
    };

    return (
        <div
            style={{ '--stack-rank-selected-row-color': selectedRowColor || 'transparent' } as React.CSSProperties}
        >
            <Menu model={items} popup ref={menu} id="popup_menu" className="headerFeatures" />
            <DataTable
                showGridlines
                stripedRows
                scrollable
                ref={tableRef}
                scrollHeight="800px"
                className="stackRankTable"
                tableStyle={{ tableLayout: 'fixed' }} // ✅ Ensures correct column width behavior
                hidden={hideTable}
                value={agents}
                key="employee_number"
                paginator={agents?.length > 50}
                header={header}
                first={first}
                rows={rows}
                onPage={(e) => {
                    setFirst(e.first);
                    setRows(e.rows);
                }}
                onRowClick={onRowClick}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                rowsPerPageOptions={[50, 100, 150]}
                dataKey={uniqueKey}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={['employee_name']}
                emptyMessage="No agents found."
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                sortOrder={1}
                rowClassName={rowClassName}
            >
                <Column
                    frozen
                    header="Rank"
                    sortable
                    sortField="overall_rank"
                    filterField="overall_rank"
                    showFilterMatchModes={false}
                    style={{ width: '5.1rem', minWidth: '5.1rem', maxWidth: '5.1rem', textAlign: 'center' }}
                    field="overall_rank"
                />
                <Column
                    frozen
                    header={scorecardRoleId === ConstScorecardRole.TEAM_LEAD ? 'Team Lead' : 'Agent'}
                    sortable
                    sortField="employee_name"
                    filterField="employee_name"
                    showFilterMatchModes={false}
                    filterMenuStyle={{ width: '20rem' }}
                    style={{ width: '18rem', minWidth: '18rem', maxWidth: '18rem', textAlign: 'left' }}
                    field="employee_name"
                    // body={(rowData) => NameBodyTemplate(rowData, 'agent')}
                    body={(rowData) => TruncatedTextTemplate(rowData)}
                />

                <Column
                    header="Comp Score"
                    sortable
                    sortField="composite_score"
                    filterField="composite_score"
                    showFilterMatchModes={false}
                    //filterMenuStyle={{ width: '20rem' }}
                    style={{ width: '5.7rem', whiteSpace: 'nowrap' }} // ✅ Fixed width
                    field="composite_score"
                    body={formatPercentage}
                />

                <Column
                    header="Attainment"
                    style={{ width: '7rem' }} // ✅ Fixed width
                    //field="composite_score"
                    body={formatAttainment}
                />

                {metricColHeader.map((metricHeader: any) => {
                    return (
                        <Column
                            key={metricHeader}
                            header={metricHeader}
                            showFilterMatchModes={false}
                            filterMenuStyle={{ width: '10rem' }}
                            style={{ width: '10rem' }}
                            body={metricsOneBodyTemplateDispatch}
                            headerClassName={getHeaderClassName(metricHeader)}
                            headerStyle={getHeaderStyle(metricHeader)}
                            sortable
                            sortField={metricHeader}
                            field={`agentMetrics[0].metricsDetails.${metricHeader}.metric_value`}
                            sortFunction={(event) => customSortFunction(event, metricHeader)}
                        />
                    );
                })}

                <Column
                    header="Team Lead"
                    sortable
                    sortField="supervisor_number"
                    filterField="supervisor_number"
                    showFilterMatchModes={false}
                    filterMenuStyle={{ width: '20rem' }}
                    style={{ width: '13rem' }} // ✅ Fixed width
                    field="supervisor_number"
                    body={(rowData) => NameBodyTemplate(rowData, 'supervisor')}
                    hidden
                />

                <Column
                    header="Manager"
                    sortable
                    sortField="manager_number"
                    filterField="manager_number"
                    showFilterMatchModes={false}
                    filterMenuStyle={{ width: '20rem' }}
                    style={{ width: '13rem' }} // ✅ Fixed width
                    field="manager_number"
                    body={(rowData) => NameBodyTemplate(rowData, 'manager')}
                    hidden
                />
                <Column frozen body={actionTemplate} style={{ minWidth: '2rem', width: '2rem' }} />
            </DataTable>
            {calculationVisible && renderCalculationModels()}
        </div>
    );
};
